import { Typography } from "@mui/material";
import List from "@mui/material/List";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { hasAnyAccessToResource } from "api/general";
import { ReactComponent as VideoRecorderIcon } from "components/Icons/video-recorder.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { SubMenu } from "types/sidebar";
import { Link } from "./Link";
import { SubMenuList } from "./SubMenuList";
import { isGlobalManagementView, isSingleMemberView, useMenu } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    list: {
      marginTop: ({ isDrawerOpen }: { isDrawerOpen?: boolean }) =>
        isDrawerOpen ? theme.spacing(4) : theme.spacing(2),
      padding: theme.spacing(0, 8),
    },
    divider: {
      backgroundColor: "#ECECEC",
      height: "1px",
      width: "100%",
      margin: theme.spacing(4, 0),
    },
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflowY: "auto",
    },
    closedList: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(2),
    },
    sectionTitle: {
      color: theme.primary800,
      fontWeight: 500,
      fontSize: 12,
      textTransform: "uppercase",
      padding: theme.spacing(0, 8),
    },
    listItem: {
      alignItems: "center",
      backgroundColor: theme.primary50,
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      gap: theme.spacing(4),
      margin: theme.spacing(4, 8),
      padding: theme.spacing(3, 4),
      textDecoration: "none",
    },
    closedDrawer: {
      backgroundColor: theme.primary100,
      height: 48,
      justifyContent: "center",
      margin: theme.spacing(0, 8),
      width: 48,
    },
    iconOpen: {
      backgroundColor: theme.primary100,
      borderRadius: "8px",
      display: "flex",
      padding: theme.spacing(4),
      "& svg": {
        color: theme.primary500,
        height: 20,
        width: 20,
      },
    },
    label: {
      color: theme.primary500,
      fontSize: 14,
      fontWeight: 500,
    },
    description: {
      color: theme.primary400,
      fontSize: 11,
      fontWeight: 400,
    },
    closed: {
      borderRadius: "8px",
      color: theme.primary500,
      display: "flex",
    },
  }),
);

export function LinksList(props: {
  isDrawerOpen?: boolean;
  onCollapseMenuClick?: () => void;
}) {
  const {
    generalLinks,
    eventsLinks,
    administratorLinks,
    loayaltyModulesLinks,
    otherLinks,
    getInitialSubMenu,
  } = useMenu();
  const [subMenu, setSubMenu] = useState<SubMenu | null>(getInitialSubMenu());
  const classes = useStyles({ isDrawerOpen: props.isDrawerOpen });
  const { message } = useMessage();
  const location = useLocation();
  const hasAnyAccessToEventsMenu =
    hasAnyAccessToResource("TRANSACTION") ||
    hasAnyAccessToResource("CUSTOM_EVENT");
  const hasAnyGlobalManagementPermission =
    hasAnyAccessToResource("GLOBAL_MANAGEMENT");

  useEffect(() => {
    setSubMenu(getInitialSubMenu());
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <div className={classes.container}>
      {subMenu ? (
        <SubMenuList
          {...subMenu}
          closeMenu={() => setSubMenu(null)}
          isDrawerOpen={!!props.isDrawerOpen}
        />
      ) : (
        <>
          {props.isDrawerOpen && <div className={classes.divider} />}
          <NavLink
            className={`${classes.listItem} ${!props.isDrawerOpen ? classes.closedDrawer : ""}`}
            data-sentry-unmask
            target="blank"
            to="https://app.guideflow.com/player/5pv0gj2fwr"
          >
            <div
              className={`${props.isDrawerOpen ? classes.iconOpen : classes.closed}`}
            >
              <VideoRecorderIcon />
            </div>
            {props.isDrawerOpen && (
              <div className={classes.container}>
                <Typography className={classes.label}>
                  {message({ id: "guideTour" })}
                </Typography>
                <Typography className={classes.description}>
                  {message({ id: "guideTourInfo" })}
                </Typography>
              </div>
            )}
          </NavLink>
          {props.isDrawerOpen && <div className={classes.divider} />}
          {hasAnyGlobalManagementPermission && (
            <>
              {props.isDrawerOpen && (
                <Typography
                  className={classes.sectionTitle}
                  data-sentry-unmask
                  mt={8}
                >
                  {message({ id: "administrator" })}
                </Typography>
              )}
              <List
                className={` ${classes.list} ${!props.isDrawerOpen ? classes.closedList : ""}`}
              >
                {administratorLinks.map((item, index) => (
                  <Link
                    details={item}
                    isDrawerOpen={props.isDrawerOpen}
                    items={item?.items}
                    setSubMenu={setSubMenu}
                    key={index}
                  />
                ))}
              </List>
            </>
          )}
          {props.isDrawerOpen && (
            <Typography
              className={classes.sectionTitle}
              mt={8}
              data-sentry-unmask
            >
              {message({ id: "general" })}
            </Typography>
          )}
          <List
            className={` ${classes.list} ${!props.isDrawerOpen ? classes.closedList : ""}`}
          >
            {generalLinks.map((item, index) => (
              <Link
                details={item}
                isDrawerOpen={props.isDrawerOpen}
                items={item?.items}
                setSubMenu={setSubMenu}
                key={index}
              />
            ))}
          </List>
          {hasAnyAccessToEventsMenu && (
            <>
              {props.isDrawerOpen && (
                <Typography
                  className={classes.sectionTitle}
                  mt={8}
                  data-sentry-unmask
                >
                  {message({ id: "events" })}
                </Typography>
              )}
              <List
                className={` ${classes.list} ${!props.isDrawerOpen ? classes.closedList : ""}`}
              >
                {eventsLinks.map((item, index) => (
                  <Link
                    details={item}
                    isDrawerOpen={props.isDrawerOpen}
                    items={item?.items}
                    setSubMenu={setSubMenu}
                    key={index}
                  />
                ))}
              </List>
            </>
          )}
          {props.isDrawerOpen && (
            <Typography
              className={classes.sectionTitle}
              mt={8}
              data-sentry-unmask
            >
              {message({ id: "loyaltyModules" })}
            </Typography>
          )}
          <List
            className={` ${classes.list} ${!props.isDrawerOpen ? classes.closedList : ""}`}
          >
            {loayaltyModulesLinks.map((item, index) => (
              <Link
                details={item}
                isDrawerOpen={props.isDrawerOpen}
                items={item?.items}
                setSubMenu={setSubMenu}
                key={index}
              />
            ))}
          </List>
        </>
      )}
      {otherLinks?.length > 0 &&
        !isGlobalManagementView(location.pathname) &&
        !isSingleMemberView(location.pathname) && (
          <>
            <div className={classes.divider}></div>
            <List
              className={` ${classes.list} ${!props.isDrawerOpen ? classes.closedList : ""}`}
            >
              {otherLinks.map((item, index) => (
                <Link
                  details={item}
                  isDrawerOpen={props.isDrawerOpen}
                  items={item?.items}
                  setSubMenu={setSubMenu}
                  key={index}
                />
              ))}
            </List>
          </>
        )}
    </div>
  );
}
